<template>
  <div class="terms-of-service">
    <div class="container">
      <div class="header">
        <h1>Terms of Service</h1>
        <p class="last-updated">Last Updated: December 24, 2025</p>
      </div>

      <div class="content">
        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using InvestBuddy.ai ("Service", "Platform", "we", "our"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use our Service.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            InvestBuddy.ai is a portfolio management and analysis platform that provides:
          </p>
          <ul>
            <li>Portfolio tracking and performance analytics</li>
            <li>Risk assessment and diversification analysis</li>
            <li>AI-powered stock predictions with LSTM machine learning models (61.18% validated accuracy)</li>
            <li>MCP (Model Context Protocol) server for AI platform integrations</li>
            <li>RESTful API for programmatic access</li>
            <li>Third-party integrations (ChatGPT Custom GPT, Claude Desktop, etc.)</li>
            <li>Price alerts and notifications</li>
            <li>Market data and stock information</li>
          </ul>
          <p class="disclaimer">
            <strong>Important:</strong> InvestBuddy.ai is an informational tool only. We do not provide investment advice, execute trades, or manage your investments. All investment decisions are your sole responsibility.
          </p>
        </section>

        <section>
          <h2>3. Investment Disclaimer</h2>
          <p>
            <strong>NOT FINANCIAL ADVICE:</strong> The information, analysis, and recommendations provided by InvestBuddy.ai are for informational and educational purposes only. They do not constitute financial, investment, trading, or other professional advice.
          </p>
          <ul>
            <li>Past performance does not guarantee future results</li>
            <li>All investments involve risk, including potential loss of principal</li>
            <li>You should consult with qualified financial advisors before making investment decisions</li>
            <li>We are not registered as a securities broker-dealer or investment adviser</li>
            <li>Our ML models and algorithms are tools for analysis, not guarantees of performance</li>
          </ul>
        </section>

        <section>
          <h2>4. User Accounts</h2>
          <h3>4.1 Registration</h3>
          <p>
            To use certain features, you must register for an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain and update your information to keep it accurate</li>
            <li>Maintain the security of your password and account</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Notify us immediately of any unauthorized use</li>
          </ul>

          <h3>4.2 Account Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account if you violate these Terms or engage in fraudulent, abusive, or illegal activities.
          </p>
        </section>

        <section>
          <h2>5. Subscription and Payments</h2>
          <h3>5.1 Free Trial</h3>
          <p>
            New users may receive a free trial period. Trial accounts have full access to premium features during the trial period. Phone verification may be required to prevent abuse.
          </p>

          <h3>5.2 Paid Subscriptions</h3>
          <p>
            Premium features require a paid subscription. Pricing is available on our Pricing page. By subscribing, you authorize us to charge your payment method on a recurring basis.
          </p>

          <h3>5.3 Cancellation and Refunds</h3>
          <p>
            You may cancel your subscription at any time. Cancellations take effect at the end of the current billing period. Refunds are handled on a case-by-case basis.
          </p>
        </section>

        <section>
          <h2>6. Data and Privacy</h2>
          <p>
            Your privacy is important to us. Our collection and use of personal information is described in our <router-link to="/privacy-policy">Privacy Policy</router-link>. By using InvestBuddy.ai, you consent to our data practices as described in the Privacy Policy.
          </p>
        </section>

        <section>
          <h2>7. Acceptable Use</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Use the Service for any illegal purpose or in violation of any laws</li>
            <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
            <li>Share, sell, or distribute your API keys to unauthorized parties</li>
            <li>Exceed rate limits or abuse API/MCP services</li>
            <li>Use the Service to manipulate markets or engage in fraudulent trading</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use automated systems (bots, scrapers) without permission (API access is provided for automation)</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Resell, redistribute, or commercialize the Service without authorization</li>
            <li>Upload viruses, malware, or malicious code</li>
            <li>Harass, abuse, or harm other users</li>
          </ul>
        </section>

        <section>
          <h2>8. API and MCP Server Usage</h2>
          <h3>8.1 API Keys</h3>
          <p>
            To access our API and MCP server, you must generate an API key from your account settings. You are responsible for:
          </p>
          <ul>
            <li>Keeping your API keys secure and confidential</li>
            <li>Not sharing API keys with unauthorized parties</li>
            <li>All activities performed using your API keys</li>
            <li>Immediately revoking compromised API keys</li>
          </ul>

          <h3>8.2 Rate Limits</h3>
          <p>
            API and MCP usage is subject to rate limits based on your subscription tier:
          </p>
          <ul>
            <li><strong>Trial:</strong> 5 prediction requests per day</li>
            <li><strong>Premium:</strong> 150 prediction requests per day</li>
            <li><strong>Business:</strong> Unlimited requests (fair use policy applies)</li>
          </ul>
          <p>
            Exceeding rate limits may result in temporary service suspension. Systematic abuse may result in account termination.
          </p>

          <h3>8.3 Third-Party Integration</h3>
          <p>
            When using InvestBuddy through third-party platforms (ChatGPT, Claude Desktop, etc.):
          </p>
          <ul>
            <li>You must comply with the third-party platform's terms of service</li>
            <li>We are not responsible for the actions or policies of third-party platforms</li>
            <li>Your data is subject to both our privacy policy and the platform's privacy policy</li>
            <li>We may discontinue integration with any platform at our discretion</li>
          </ul>

          <h3>8.4 MCP Server</h3>
          <p>
            Our MCP (Model Context Protocol) server is available for use with compatible AI clients. By using the MCP server, you acknowledge:
          </p>
          <ul>
            <li>The MCP protocol is maintained by Anthropic and subject to change</li>
            <li>We provide the server "as is" without guarantees of compatibility with all MCP clients</li>
            <li>MCP access requires a valid API key and active subscription</li>
            <li>All prediction data is provided for informational purposes only</li>
          </ul>
        </section>

        <section>
          <h2>9. Intellectual Property</h2>
          <p>
            All content, features, and functionality of InvestBuddy.ai, including but not limited to text, graphics, logos, algorithms, software, and machine learning models, are owned by InvestBuddy.ai and protected by international copyright, trademark, and other intellectual property laws.
          </p>
          <p>
            You are granted a limited, non-exclusive, non-transferable license to use the Service for personal, non-commercial purposes.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Services and Data</h2>
          <p>
            InvestBuddy.ai integrates with third-party services for market data, news, and other features. We are not responsible for:
          </p>
          <ul>
            <li>The accuracy, completeness, or timeliness of third-party data</li>
            <li>The availability or performance of third-party services</li>
            <li>The privacy practices of third-party services</li>
          </ul>
          <p>
            Market data may be delayed and should not be relied upon for time-sensitive trading decisions.
          </p>
        </section>

        <section>
          <h2>11. Disclaimers and Limitation of Liability</h2>
          <h3>10.1 Service "As Is"</h3>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
          </p>

          <h3>10.2 No Guarantee</h3>
          <p>
            We do not guarantee that:
          </p>
          <ul>
            <li>The Service will be uninterrupted, timely, secure, or error-free</li>
            <li>Data will be accurate, reliable, or complete</li>
            <li>Defects will be corrected</li>
            <li>The Service is free of viruses or harmful components</li>
          </ul>

          <h3>10.3 Limitation of Liability</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, INVESTBUDDY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING FROM YOUR USE OF THE SERVICE.
          </p>
          <p>
            OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section>
          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless InvestBuddy.ai, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
          </p>
          <ul>
            <li>Your use of the Service, API, or MCP server</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Your investment decisions based on information from the Service</li>
            <li>Unauthorized use or disclosure of your API keys</li>
            <li>Your use of third-party integrations (ChatGPT, Claude, etc.)</li>
          </ul>
        </section>

        <section>
          <h2>13. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of material changes via email or through the Service. Your continued use of the Service after changes constitutes acceptance of the modified Terms.
          </p>
        </section>

        <section>
          <h2>14. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions.
          </p>
          <p>
            Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration, except where prohibited by law.
          </p>
        </section>

        <section>
          <h2>15. Contact Information</h2>
          <p>
            For questions about these Terms, please contact us:
          </p>
          <ul>
            <li>Email: support@investbuddy.ai</li>
            <li>Contact Form: <router-link to="/contact">Contact Us</router-link></li>
          </ul>
        </section>

        <section>
          <h2>15. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
          </p>
        </section>

        <section>
          <h2>16. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy and Cookie Policy, constitute the entire agreement between you and InvestBuddy.ai regarding the use of the Service.
          </p>
        </section>
      </div>

      <div class="footer-actions">
        <router-link to="/" class="btn-secondary">Back to Home</router-link>
        <router-link to="/contact" class="btn-primary">Contact Us</router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

onMounted(() => {
  window.scrollTo(0, 0)
})
</script>

<style scoped>
.terms-of-service {
  min-height: 100vh;
  background: #f9fafb;
  padding: 2rem 0;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
}

.header {
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
}

.header h1 {
  font-size: 2.5rem;
  color: #111827;
  margin-bottom: 0.5rem;
}

.last-updated {
  color: #6b7280;
  font-size: 0.9rem;
}

.content {
  background: #ffffff;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

section {
  margin-bottom: 2.5rem;
}

section:last-child {
  margin-bottom: 0;
}

h2 {
  font-size: 1.5rem;
  color: #111827;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

h3 {
  font-size: 1.2rem;
  color: #111827;
  margin: 1.5rem 0 0.75rem 0;
}

p {
  color: #4b5563;
  line-height: 1.8;
  margin-bottom: 1rem;
}

ul {
  color: #4b5563;
  line-height: 1.8;
  margin: 1rem 0 1rem 2rem;
}

li {
  margin-bottom: 0.5rem;
}

.disclaimer {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 1rem;
  margin: 1.5rem 0;
  border-radius: 4px;
  color: #856404;
}

.disclaimer strong {
  display: block;
  margin-bottom: 0.5rem;
}

a {
  color: #2563eb;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.footer-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #e5e7eb;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
  text-decoration: none;
  transform: translateY(-2px);
}

.btn-secondary {
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f3f4f6;
  text-decoration: none;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .header h1 {
    font-size: 2rem;
  }

  .content {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.3rem;
  }

  .footer-actions {
    flex-direction: column;
  }
}
</style>
